import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import Hero from './components/Hero';
import SmartFeatures from './components/SmartFeatures';
import ModularStructure from './components/ModularStructure';
import InteriorSpaces from './components/InteriorSpaces';
import UseCases from './components/UseCases';
import TechnicalSpecs from './components/TechnicalSpecs';
import PlanYourPodProject from './components/PlanYourPodProject';
import AboutUs from './components/AboutUs';
import Footer from './components/Footer';
import CoreProductRanges from './components/CoreProductRanges';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <PageSEO
        title="Nexa Pods | Modular Living Pods Melbourne Australia"
        description="Explore Nexa Pods modular living pods in Melbourne for premium stays, workspaces and property projects across Victoria and Australia."
        canonicalPath="/"
        keywords="Nexa Pods, space capsule living, modular pods Kew, smart cabins Australia"
      />
      <div className="bg-gradient-to-b from-[#0A0A0A] via-[#0D1117] to-[#000000] text-white min-h-screen w-full overflow-x-hidden">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${
          scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-cyan-500/20' : 'bg-transparent'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex items-center justify-between w-full">
            <Link to="/" className="flex items-center gap-3 flex-shrink-0">
              <img 
                src="/images/c719b13a28cc.webp" 
                alt="Space Capsule Living" 
                className="h-16 sm:h-20 lg:h-28 w-auto"
              />
            </Link>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              {/* Products Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setProductsDropdownOpen(true)}
                onMouseLeave={() => setProductsDropdownOpen(false)}
              >
                <Link 
                  to="/product-range"
                  className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  Products
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </Link>
                
                {productsDropdownOpen && (
                  <div className="absolute top-full left-0 pt-2 w-auto">
                    <div className="bg-black/90 backdrop-blur-xl border border-cyan-500/30 rounded-lg shadow-[0_0_20px_rgba(0,217,255,0.3)] overflow-hidden">
                      <Link 
                        to="/product-range" 
                        className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                      >
                        All Products
                      </Link>
                      <Link 
                        to="/a-range-details" 
                        className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                      >
                        A Series - Workplace Solution
                      </Link>
                      <Link 
                        to="/x-range-details" 
                        className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                      >
                        X Series - Luxury Living
                      </Link>
                      <Link 
                        to="/m-range-details" 
                        className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                      >
                        M Series - Modularize
                      </Link>
                      <Link 
                        to="/z-range-details" 
                        className="block px-6 py-3 text-base text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                      >
                        Z Series - Coming Soon
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <a href="#features" className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer">Features</a>
              <a href="#interior" className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer">Interior</a>
              <a href="#specs" className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer">Specifications</a>
              <a href="#plan" className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer">Plan Your Project</a>
              <a href="#about" className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer">About</a>
              <Link to="/granny-flats-victoria" className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer">Granny Flats</Link>
              <Link to="/contact" className="px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer">
                Contact Us
              </Link>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-cyan-500/20 w-full">
              <div className="px-4 py-6 space-y-4 w-full">
                {/* Mobile Products Dropdown */}
                <div>
                  <button 
                    onClick={() => setProductsDropdownOpen(!productsDropdownOpen)}
                    className="w-full flex items-center justify-between text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer"
                  >
                    Products
                    <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  
                  {productsDropdownOpen && (
                    <div className="mt-2 ml-4 space-y-2 bg-black/90 border border-cyan-500/30 rounded-lg shadow-[0_0_15px_rgba(0,217,255,0.2)] overflow-hidden">
                      <Link 
                        to="/product-range" 
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                      >
                        All Products
                      </Link>
                      <Link 
                        to="/a-range-details" 
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                      >
                        A Series - Workplace Solution
                      </Link>
                      <Link 
                        to="/x-range-details" 
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                      >
                        X Series - Luxury Living
                      </Link>
                      <Link 
                        to="/m-range-details" 
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                      >
                        M Series - Modularize
                      </Link>
                      <Link 
                        to="/z-range-details" 
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                      >
                        Z Series - Coming Soon
                      </Link>
                    </div>
                  )}
                </div>

                <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Features</a>
                <a href="#interior" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Interior</a>
                <a href="#specs" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Specifications</a>
                <a href="#plan" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Plan Your Project</a>
                <a href="#about" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">About</a>
                <Link to="/granny-flats-victoria" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Granny Flats</Link>
                <Link to="/contact" className="block w-full text-center px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all whitespace-nowrap cursor-pointer">
                  Contact Us
                </Link>
              </div>
            </div>
          )}
        </nav>

        <Hero />
        <CoreProductRanges />
        <SmartFeatures />
        <ModularStructure />
        <InteriorSpaces />
        <UseCases />
        <TechnicalSpecs />
        <PlanYourPodProject />
        <AboutUs />
        <Footer />
      </div>
    </>
  );
}
